/**
 * @description HTTP 请求封装
 **/
import axios from 'axios'
import Cookies from 'js-cookie'
import { showToast } from 'vant'

// 后端返回的数据类型
export interface ResOptions<T> {
  data: T
  code?: number
  status: T
  errorMsg?: string
  errorCode: T
}

// 创建 axios 实例
const instance = axios.create({
  timeout: 30000,
})

// 清除登录态
const clearAuth = () => {
  Cookies.remove('token')
  Cookies.remove('userInfo')
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('uid')
    sessionStorage.removeItem('userInfo')
  }
}

// 从 URL query 中读取参数（兼容原有逻辑）
const getQueryParam = (key: string): string => {
  if (typeof window === 'undefined') return ''
  const search = window.location.search.slice(1)
  const params = new URLSearchParams(search)
  return params.get(key) || ''
}

// 构建请求 headers
const buildHeaders = (extraHeaders?: Record<string, string>) => {
  const cookieToken = Cookies.get('token')
  const sessionToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null
  const token = cookieToken || sessionToken || getQueryParam('token') || ''
  const lang = Cookies.get('lang') || getQueryParam('lang') || import.meta.env.VITE_LANG || 'zh_CN'
  const sysType =
    Cookies.get('sysType') ||
    getQueryParam('sysType') ||
    getQueryParam('isFrom') ||
    getQueryParam('is_From') ||
    'M'

  return {
    token,
    Lan: lang,
    sysType,
    ...extraHeaders,
  }
}

// 处理响应结果
const handleResult = (result: ResOptions<any>): any => {
  if (result.errorCode === 3000 || result.errorCode === 3001) {
    clearAuth()
    return Promise.reject(new Error('Authentication failed'))
  }
  // 兼容新 /shop API 格式：{ code: 200, msg: 'success', data: ... }
  // 兼容 worldcup 等 API 格式：{ code: 0, msg: 'success', data: ... }
  const r = result as any
  if (typeof r.code === 'number') {
    if (r.code === 200 || r.code === 0) {
      return { status: 'success', data: r.data }
    } else {
      if (r.msg) showToast(r.msg)
      return { status: 'fail', data: null, code: r.code }
    }
  }
  if (result && result.status === 'success') {
    return result
  } else {
    if (result.errorMsg) showToast(result.errorMsg)
    return result
  }
}

const fetch = async (url: string, options?: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const headers = buildHeaders(extraHeaders)

  try {
    const method = (options?.method || 'get').toLowerCase()
    const axiosConfig: any = {
      url,
      method,
      headers,
    }

    if (method === 'get') {
      axiosConfig.params = options?.params
    } else {
      axiosConfig.data = options?.body
    }

    const response = await instance.request(axiosConfig)
    const result = response.data as ResOptions<any>
    return handleResult(result)
  } catch (error: any) {
    // axios 错误或网络错误
    const data = error?.response?.data
    if (data) {
      if (data.errorCode === 3000 || data.errorCode === 3001) {
        clearAuth()
        return Promise.reject(new Error('Authentication failed'))
      }
      if (data.errorMsg) showToast(data.errorMsg)
      return Promise.reject(data)
    }
    return Promise.reject(error)
  }
}

export default new (class useRequest {
  get(url: string, params?: any, headers?: any): Promise<any> {
    return fetch(url, { method: 'get', params }, headers)
  }

  post(url: string, body?: any, headers?: any): Promise<any> {
    return fetch(url, { method: 'post', body }, headers)
  }

  put(url: string, body?: any, headers?: any): Promise<any> {
    return fetch(url, { method: 'put', body }, headers)
  }

  delete(url: string, body?: any, headers?: any): Promise<any> {
    return fetch(url, { method: 'delete', body }, headers)
  }
})()
